import { useEffect } from 'react';

import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/useToast';
import { ChooseOrganizationForm } from './ChooseOrganizationForm';
import { useAuth } from './useAuth';

/**
 * Tells the person when the organization open in this tab is no longer
 * theirs (portal-session-rules, B14, D16).
 *
 * With one organization left, the portal has already moved them there and
 * says so in a toast. With several, it asks which to open, in a dialog that
 * cannot be dismissed without a choice - every page behind it would answer
 * not-found for the organization they lost.
 *
 * Its own component, rendered inside the signed-in part of the app, because
 * AuthProvider sits outside the toast provider and the router.
 */
export function OrganizationAccessNotice() {
  const {
    organizationNotice: notice,
    dismissOrganizationNotice,
    switchOrganization,
  } = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (notice?.kind !== 'switched') return;
    toast.show({
      message: `You no longer have access to ${notice.lostName}.`,
      // Neither a success nor an error: the person did nothing wrong.
      severity: 'info',
      testId: 'organization-access-lost-message',
    });
    dismissOrganizationNotice();
  }, [notice, toast, dismissOrganizationNotice]);

  if (notice?.kind !== 'choose') return null;

  return (
    <Dialog
      open
      title="Choose an organization"
      description={`You no longer have access to ${notice.lostName}.`}
      dismissible={false}
      testId="organization-access-dialog"
    >
      <ChooseOrganizationForm
        testId="organization-access"
        memberships={notice.memberships}
        onChoose={(chosen) => {
          switchOrganization(chosen.id);
          dismissOrganizationNotice();
        }}
      />
    </Dialog>
  );
}
